import React from 'react';
import clsx from 'clsx';
import { makeStyles, Theme } from '@material-ui/core/styles';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import SortArrowIcon from '../icons/SortArrowIcon';
import NamedBox from '../atoms/NamedBox';
import { COLOR_BACKGROUND_WARM, COLOR_BACKGROUND_LIGHT, COLOR_BORDER, COLOR_MIDDLE_GRAY, COLOR_TEXT, COLOR_WHITE, COLOR_BLUE } from '../styles/colors';
import { FONT_PROXIMA_NOVA } from '../styles/fonts';
import { useTableSort } from '../hooks/useTableSort';
import { HighSchoolProductionResponse, HighSchoolProductionRow } from '../types/HighSchoolProductionData';

interface HighSchoolProductionTableProps {
  className?: string;
  hsProductionData?: HighSchoolProductionResponse | null;
  playerId?: number;
}

const useStyles = makeStyles((theme: Theme) => {
  const firstColWidth = 80;
  const secondColWidth = 270;
  const thirdColWidth = 60;
  const topHeaderHeight = 64;

  return {
    root: {
      border: 0,
    },
    hideNamedBoxHeader: {
      display: 'none',
    },
    headerName: {
      fontFamily: FONT_PROXIMA_NOVA,
    },
    tableContainer: {
      maxHeight: 360,
      overflow: 'auto',
    },
    table: {
      width: '100%',
      minWidth: 960,
      borderCollapse: 'collapse' as const,
      border: `1px solid ${COLOR_BACKGROUND_WARM}`,
      fontFamily: FONT_PROXIMA_NOVA,
      fontSize: theme.typography.pxToRem(16),
    },
    tableTitleCell: {
      backgroundColor: COLOR_BACKGROUND_WARM,
      height: topHeaderHeight,
      padding: theme.spacing(1, 2),
      textAlign: 'left' as const,
      whiteSpace: 'nowrap',
      ...theme.typography.h2,
      fontWeight: 700,
      boxSizing: 'border-box' as const,
    },
    stickyHeaderTop: {
      position: 'sticky' as const,
      top: 0,
      zIndex: 3,
    },
    theadGroupCell: {
      verticalAlign: 'bottom',
      backgroundColor: COLOR_BACKGROUND_WARM,
      borderLeft: `1px solid ${COLOR_BACKGROUND_WARM}`,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      height: 64,
      padding: 0,
      boxSizing: 'border-box' as const,
    },
    tableHeaderBlock: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 36,
      padding: theme.spacing(1),
      lineHeight: 1.2,
      backgroundColor: COLOR_MIDDLE_GRAY,
    },
    theadCell: {
      overflow: 'hidden',
      border: `1px solid ${COLOR_BACKGROUND_WARM}`,
      borderTop: `2px solid ${COLOR_MIDDLE_GRAY}`,
      padding: theme.spacing(1),
      whiteSpace: 'nowrap' as const,
      color: COLOR_TEXT,
      fontWeight: 700,
      textAlign: 'center' as const,
      userSelect: 'none' as const,
      cursor: 'pointer',
    },
    stickyHeaderSecond: {
      position: 'sticky' as const,
      zIndex: 2,
      top: topHeaderHeight,
      backgroundColor: COLOR_WHITE,
    },
    sortLabel: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: theme.spacing(0.25),
    },
    sortIcon: {
      opacity: 1,
      color: COLOR_MIDDLE_GRAY,

      '& svg': {
        display: 'block',
        width: 9,
        height: 'auto',
      },
    },
    thFirstCol: {
      zIndex: 3,
      left: 0,
      width: firstColWidth,
    },
    thSecondCol: {
      width: secondColWidth,
      whiteSpace: 'normal',
      textAlign: 'left',
    },
    thThirdCol: {
      width: thirdColWidth,
      whiteSpace: 'normal',
      textAlign: 'left',
    },
    stickyCol: {
      position: 'sticky' as const,
    },
    bodyRow: {
    },
    bodyCell: {
      padding: theme.spacing(1.5, 1),
      border: `1px solid ${COLOR_BACKGROUND_WARM}`,
      textAlign: 'center' as const,
      whiteSpace: 'nowrap' as const,
      backgroundColor: COLOR_WHITE,
      fontWeight: 600,
      
      'tr:nth-child(odd) &': {
        backgroundColor: COLOR_BACKGROUND_LIGHT,
      },
    },
    bodyCellFirst: {
      position: 'sticky' as const,
      left: 0,
    },
    bodyCellSecond: {
      textAlign: 'left' as const,
      whiteSpace: 'normal',
      fontWeight: 700,
    },
  };
}, { name: 'HighSchoolProductionTable' });

export default function HighSchoolProductionTable(props: HighSchoolProductionTableProps) {
  const { className, hsProductionData } = props;
  const classes = useStyles();

  const rows: HighSchoolProductionRow[] = hsProductionData?.hsProductionData || [];
  const { sortKey, sortDirection, handleSort, sortedRows } = useTableSort<HighSchoolProductionRow, keyof HighSchoolProductionRow>({
    rows,
    initialSortKey: 'year',
    initialDirection: 'desc',
    nextKeyDirection: 'desc',
  });

  function renderSortableHeader(
    label: string,
    key: keyof HighSchoolProductionRow,
    className: string,
    rowSpan?: number,
  ) {
    const isActive = sortKey === key;
    return (
      <th className={className} rowSpan={rowSpan}>
        <TableSortLabel
          active={isActive}
          direction={isActive ? sortDirection : 'desc'}
          className={classes.sortLabel}
          hideSortIcon={false}
          IconComponent={(iconProps) => (
            <SortArrowIcon
              {...iconProps}
              className={`${iconProps.className} ${classes.sortIcon}`}
              style={{
                color: isActive ? COLOR_BLUE : '#b5b5b5',
              }}
            />
          )}
          onClick={() => handleSort(key)}
        >
          {label}
        </TableSortLabel>
      </th>
    );
  }

  return (
    <NamedBox
      className={clsx(classes.root, className)}
      headerClassName={classes.hideNamedBoxHeader}
      name=""
    >
      <div className={classes.tableContainer}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th
                className={clsx(classes.stickyHeaderTop, classes.tableTitleCell)}
                colSpan={3}
              >
                High School Production
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={5}
              >
                <div className={classes.tableHeaderBlock}>Passing</div>
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={3}
              >
                <div className={classes.tableHeaderBlock}>Rushing</div>
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={3}
              >
                <div className={classes.tableHeaderBlock}>Receiving</div>
              </th>
              <th
                className={clsx(classes.theadGroupCell, classes.stickyHeaderTop)}
                colSpan={3}
              >
                <div className={classes.tableHeaderBlock}>Defense</div>
              </th>
            </tr>
            <tr>
              {renderSortableHeader(
                'Year',
                'year',
                clsx(
                  classes.theadCell,
                  classes.stickyHeaderSecond,
                  classes.stickyCol,
                  classes.thFirstCol,
                ),
              )}
              {renderSortableHeader(
                'Team',
                'hsName',
                clsx(
                  classes.theadCell,
                  classes.stickyHeaderSecond,
                  classes.thSecondCol,
                ),
              )}
              {renderSortableHeader(
                'G',
                'gamesPlayed',
                clsx(
                  classes.theadCell,
                  classes.stickyHeaderSecond,
                  classes.thThirdCol
                )
              )}
              {/* Passing */}
              {renderSortableHeader('Comp', 'passingComp', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Att', 'passingAtt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Yds', 'passingYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('TD', 'passingTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('INT', 'passingInt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {/* Rushing */}
              {renderSortableHeader('Att', 'rushingAtt', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Yds', 'rushingYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('TD', 'rushingTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {/* Receiving */}
              {renderSortableHeader('Rec', 'receiving', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Yds', 'receivingYds', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('TD', 'receivingTD', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {/* Defense */}
              {renderSortableHeader('Tckls', 'tackles', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('Sacks', 'sacks', clsx(classes.theadCell, classes.stickyHeaderSecond))}
              {renderSortableHeader('INTs', 'interceptions', clsx(classes.theadCell, classes.stickyHeaderSecond))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row) => (
              <tr key={`${row.year}-${row.hsName}`} className={classes.bodyRow}>
                <td className={clsx(classes.bodyCell, classes.bodyCellFirst)}>
                  {row.year}
                </td>
                <td className={clsx(classes.bodyCell, classes.bodyCellSecond)}>
                  {row.hsName}
                </td>
                <td className={classes.bodyCell}>{row.gamesPlayed}</td>
                {/* Passing */}
                <td className={classes.bodyCell}>{row.passingComp}</td>
                <td className={classes.bodyCell}>{row.passingAtt}</td>
                <td className={classes.bodyCell}>{row.passingYds}</td>
                <td className={classes.bodyCell}>{row.passingTD}</td>
                <td className={classes.bodyCell}>{row.passingInt}</td>
                {/* Rushing */}
                <td className={classes.bodyCell}>{row.rushingAtt}</td>
                <td className={classes.bodyCell}>{row.rushingYds}</td>
                <td className={classes.bodyCell}>{row.rushingTD}</td>
                {/* Receiving */}
                <td className={classes.bodyCell}>{row.receiving}</td>
                <td className={classes.bodyCell}>{row.receivingYds}</td>
                <td className={classes.bodyCell}>{row.receivingTD}</td>
                {/* Defense */}
                <td className={classes.bodyCell}>{row.tackles}</td>
                <td className={classes.bodyCell}>{row.sacks}</td>
                <td className={classes.bodyCell}>{row.interceptions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </NamedBox>
  );
}

